/**
 * Reading the audit log.
 *
 * The rows are written by four different modules with no shared vocabulary
 * beyond a dotted event name, so this module's whole job is to make a stream of
 * `action.confirmed` / `brand.updated` / `kb_document.ingested` legible without
 * anyone having to know which module wrote which.
 *
 * The event name is treated as data, not as a closed enum. A new module writing
 * a new event must appear in this list immediately and legibly — falling back
 * to a humanised version of the raw name — rather than being silently dropped
 * or rendered as "Unknown". A trail that quietly omits events it does not
 * recognise is worse than no trail.
 */

export interface AuditChange {
  field: string;
  before: string | null;
  after: string | null;
}

export interface ApiAuditEntry {
  id: string;
  event: string;
  entityType: string | null;
  entityId: string | null;
  createdAt: string;
  before: unknown;
  after: unknown;
  changes: AuditChange[];
  brand: { slug: string; name: string } | null;
  user: { email: string; name: string | null } | null;
}

export interface AuditPage {
  entries: ApiAuditEntry[];
  nextCursor: string | null;
}

/** The severity a row is rendered with. Colour here is meaning, not decoration. */
export type AuditTone = 'fact' | 'held' | 'stop' | 'neutral';

/**
 * Events worth colouring, keyed by exact name.
 *
 * Only outcomes that a person would scan for are given a tone: money that
 * moved, money that was refused, access that changed. Everything else stays
 * neutral so that the coloured rows actually stand out.
 */
const TONES: Record<string, AuditTone> = {
  'action.confirmed': 'fact',
  'action.approved': 'held',
  'action.failed': 'stop',
  'action.rejected': 'neutral',
  'action.blocked': 'stop',
  'membership.granted': 'held',
  'membership.revoked': 'stop',
};

export function toneFor(event: string): AuditTone {
  return TONES[event] ?? 'neutral';
}

/**
 * A readable label for an event.
 *
 * `action.confirmed` becomes "Action confirmed". An unrecognised name still
 * reads as English rather than as a key, because the alternative is a screen
 * that hides whatever it was not taught about.
 */
export function eventLabel(event: string): string {
  const [subject, ...rest] = event.split('.');
  const verb = rest.join(' ').replace(/[._]/g, ' ');
  const noun = (subject ?? event).replace(/[._]/g, ' ');
  const text = verb ? `${noun} ${verb}` : noun;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** The part before the first dot: `action`, `brand`, `kb_document`. */
export function eventFamily(event: string): string {
  return event.split('.')[0] ?? event;
}

/**
 * Who did it.
 *
 * A row with no user is the system acting on its own — the intake worker, the
 * agent processor — and that is said explicitly. "—" would read as missing
 * data when in fact "nobody" is the correct and interesting answer.
 */
export function actorLabel(entry: ApiAuditEntry): string {
  if (!entry.user) return 'System';
  return entry.user.name ?? entry.user.email;
}

/**
 * The one-line summary of the row.
 *
 * Prefers the changed fields, because that is what someone opening this screen
 * came for. A creation event has no `before` and so lists what was set.
 */
export function summarise(entry: ApiAuditEntry): string {
  if (entry.changes.length === 0) return 'No field-level detail recorded.';

  return entry.changes
    .slice(0, 3)
    .map((change) =>
      change.before === null
        ? `${change.field} set to ${change.after}`
        : change.after === null
          ? `${change.field} cleared`
          : `${change.field}: ${change.before} → ${change.after}`,
    )
    .join(' · ');
}

/**
 * Group consecutive entries by calendar day, for the date rules in the list.
 *
 * Consecutive rather than by-key: the API returns them newest-first and already
 * ordered, so grouping this way cannot reorder anything. A sort here would be a
 * second opinion about ordering and could disagree with the cursor paging.
 */
export function groupByDay(entries: readonly ApiAuditEntry[]): Array<{
  day: string;
  entries: ApiAuditEntry[];
}> {
  const groups: Array<{ day: string; entries: ApiAuditEntry[] }> = [];

  for (const entry of entries) {
    const day = entry.createdAt.slice(0, 10);
    const last = groups[groups.length - 1];
    if (last && last.day === day) last.entries.push(entry);
    else groups.push({ day, entries: [entry] });
  }

  return groups;
}
